import type { ColumnDef, Row } from "@tanstack/react-table";
import type { RowSpanInfo } from "./types";

export const getRowSpanKeys = <TData,>(columns: ColumnDef<TData, unknown>[]): string[] =>
    columns
        .map((column) => column.meta?.rowSpanBy)
        .filter((key): key is string => Boolean(key));

export const sortRowsByRowSpanKeys = <TData,>(rows: Row<TData>[], keys: string[]): Row<TData>[] => {
    if (!keys.length) return rows;

    return [...rows].sort((a, b) => {
        for (const key of keys) {
            const aVal = (a.original as Record<string, unknown>)?.[key] ?? "";
            const bVal = (b.original as Record<string, unknown>)?.[key] ?? "";
            if (aVal !== bVal) {
                return String(aVal).localeCompare(String(bVal));
            }
        }
        return 0;
    });
};

export const buildRowSpanMaps = <TData,>(
    rows: Row<TData>[],
    keys: string[]
): Record<string, Map<string, RowSpanInfo>> => {
    const result: Record<string, Map<string, RowSpanInfo>> = {};

    keys.forEach((key) => {
        const map = new Map<string, RowSpanInfo>();
        result[key] = map;

        let currentValue: unknown = null;
        let groupStartIndex = 0;

        rows.forEach((row, index) => {
            const value = (row.original as Record<string, unknown>)?.[key];

            if (value !== currentValue) {
                if (index > groupStartIndex) {
                    map.set(rows[groupStartIndex].id, { isFirst: true, span: index - groupStartIndex });
                }
                currentValue = value;
                groupStartIndex = index;
            }

            map.set(row.id, { isFirst: false, span: 0 });
        });

        const span = rows.length - groupStartIndex;
        if (span > 0) {
            map.set(rows[groupStartIndex].id, { isFirst: true, span });
        }
    });

    return result;
};

export const isGroupedContinuationRow = <TData,>(
    row: Row<TData>,
    rowSpanMaps: Record<string, Map<string, RowSpanInfo>>
): boolean =>
    Object.values(rowSpanMaps).some((map) => {
        const info = map.get(row.id);
        return info && !info.isFirst && info.span === 0;
    });
